import { lowerCase, get, isFunction, find, has, map, isEmpty, forEach, includes, filter } from 'lodash-es';
import { P as WindowEventMap, Q as eventPath, C as BXF_FOCUS_KEYBOARD_ONLY, A as BXF_MOUSE_FOCUS, a as addEventListenerOnElement, H as HOVER_IN_DELAY, d as removeFromContainer, e as elementExists, c as createElementWithClass, b as getElement, E as DEFAULT_Z_INDEX, t as isKeyboardClickEvent } from './bxf-utils-3908c3c5.js';
import { F as FocusIndexManager } from './focus-index-manager-d376b5ea.js';
import { B as BxfTypeAheadConstants } from './bxf-type-ahead.constants-d006f018.js';
import { D as DefaultFlipOrder, a as BxfDynamicPositionEnum, B as BxfChatCanvasConstants } from './bxf-chat-canvas.constants-0879888b.js';
import { B as BxfPositionConstants, F as FocusTrap } from './focus-trap-023cd6d1.js';
import { K as KeyboardEventKey } from './keyboard-event-key.enum-2f6c4d91.js';

const { BOTTOM_PADDING, POPOVER_TIP_OFFSET } = BxfPositionConstants.STRING;
const { BOTTOM_CENTER, BOTTOM_LEFT, BOTTOM_RIGHT, TOP_CENTER, TOP_LEFT, TOP_RIGHT, LEFT_BOTTOM, LEFT_CENTER, LEFT_TOP, RIGHT_BOTTOM, RIGHT_CENTER, RIGHT_TOP, } = BxfDynamicPositionEnum;
class BxfDynamicPosition {
    modifiers;
    source;
    target;
    xPos = 0;
    yPos = 0;
    position;
    spacingBelow = 0;
    _isContextMenu = false;
    isSmall = false;
    constructor(modifiers, source, target) {
        this.modifiers = modifiers;
        this.source = source;
        this.target = target;
        this.modifiers = {
            isOnCursorPosition: false,
            flipOrder: DefaultFlipOrder,
            defaultPlacement: BxfDynamicPositionEnum.BOTTOM_CENTER,
            mouseCoordinates: null,
            padding: 0,
            viewportPadding: 5,
            scrollToFit: false,
            elementHeightStatus: {
                originalElementHeight: BxfPositionConstants.STRING.INITIAL_ELEMENT_HEIGHT,
            },
            ...this.modifiers,
        };
        this.updatePosition();
        if (this.isSmall) {
            this.isSmall = false;
            this.updatePosition();
        }
    }
    padding;
    viewportBoundaries;
    sourceElBoundingRect;
    targetElBoundingRect;
    getPositionClass(baseClass) {
        if (this.position) {
            const splitArr = lowerCase(this.position).split(' ');
            const splitArrayClass = `${splitArr[0].toLowerCase()}-${splitArr[1].toLowerCase()}`;
            return `${baseClass}--${splitArrayClass}`;
        }
    }
    getElementsBoundingClientRect(element) {
        if (element) {
            const shadowRootElement = Array.from(get(element, 'shadowRoot.children') || [])
                .filter((elm) => get(elm, 'nodeName') !== 'STYLE')
                .pop();
            if (shadowRootElement) {
                return shadowRootElement.getBoundingClientRect();
            }
            return element.getBoundingClientRect();
        }
    }
    updatePosition() {
        this.sourceElBoundingRect = this.getElementsBoundingClientRect(this.source);
        this.targetElBoundingRect = this.modifiers.isOnCursorPosition
            ? this.getMouseBoundingClientRect()
            : this.getElementsBoundingClientRect(this.target);
        this.viewportBoundaries = this.getViewPortBoundaries();
        if (isFunction(this.modifiers.padding)) {
            this.padding = this.modifiers.padding() || 0;
        }
        else {
            this.padding = this.modifiers.padding ?? 0;
        }
        this.determinePosition(this.modifiers.flipOrder, this.modifiers.defaultPlacement);
        this.setPosition();
    }
    determinePosition(flipOrder, defaultPlacement) {
        let pos = defaultPlacement;
        let overflows = true;
        const defaultPlacementIsValid = this.isPositionValid(defaultPlacement);
        const defaultPos = {
            x: this.xPos,
            y: this.yPos,
        };
        if (defaultPlacementIsValid) {
            overflows = false;
        }
        else {
            for (const flipPos of flipOrder) {
                if (flipPos !== defaultPlacement &&
                    this.isPositionValid(flipPos)) {
                    pos = flipPos;
                    overflows = false;
                    break;
                }
            }
        }
        // None of the flipOrder worked. It means that the overlay is overflowing.
        if (overflows) {
            // If the overlay is overflowing (i.e. it has a vertical scroll), then we should ignore bottom checks and check for left or right positions.
            const newFlipOrder = flipOrder.filter((u) => u.toUpperCase().startsWith('LEFT') ||
                u.toUpperCase().startsWith('RIGHT')) || [];
            for (const order of newFlipOrder) {
                if (this.isPositionValid(order, true)) {
                    pos = order;
                    overflows = false;
                    break;
                }
            }
            // When none of the scenarios satisfy, fallback to show overlay container in default position with scroll
            if (overflows) {
                this.xPos = defaultPos.x;
                this.yPos = defaultPos.y;
            }
        }
        this.position = pos;
        return { left: this.xPos, top: this.yPos };
    }
    updateOverlayElementSize(overlayElement, updatedHeight) {
        overlayElement.style.height = `${updatedHeight / 16}rem`;
        overlayElement.style.bottom = `${BOTTOM_PADDING / 16}rem`;
    }
    shrinkOverflowingOverlayElement(overlayElement, visibleHeightOffset) {
        const overlayStyle = overlayElement.classList.contains(BxfTypeAheadConstants.STRING.DROPDOWN)
            ? 'hidden'
            : 'auto';
        overlayElement.style.overflowY = overlayStyle;
        this.updateOverlayElementSize(overlayElement, visibleHeightOffset);
    }
    determineIfOverlayElementShouldGrow(overlayElement, currentlyFocusedOverlayElementHeight, visibleHeightOffset) {
        // If the current element height is smaller than its size prior to shrinking, it should grow
        if (currentlyFocusedOverlayElementHeight <
            this.modifiers.elementHeightStatus.originalElementHeight) {
            this.growOverlayElement(overlayElement, currentlyFocusedOverlayElementHeight, visibleHeightOffset);
        }
    }
    growOverlayElement(overlayElement, currentlyFocusedOverlayElementHeight, visibleHeightOffset) {
        const differenceBetweenCurrentAndoriginalElementHeight = visibleHeightOffset - currentlyFocusedOverlayElementHeight;
        const newOverlayElementSize = currentlyFocusedOverlayElementHeight +
            differenceBetweenCurrentAndoriginalElementHeight;
        if (newOverlayElementSize <=
            this.modifiers.elementHeightStatus.originalElementHeight) {
            this.updateOverlayElementSize(overlayElement, newOverlayElementSize);
        }
        else {
            // Prevent the element from growing larger than its size prior to being shrunken
            this.updateOverlayElementSize(overlayElement, this.modifiers.elementHeightStatus.originalElementHeight);
        }
    }
    determineIfOverlayElementCanResizeWithoutOverflow(currentlyFocusedOverlayElementHeight, visibleHeightOffset) {
        if (currentlyFocusedOverlayElementHeight !==
            this.modifiers.elementHeightStatus.originalElementHeight) {
            /**
             * If the currentlyFocusedOverlayElementHeight and the originalElementHeight are smaller
             * than the visibleHeightOffset it means that we have enough space for the element to
             * shrink/grow further. We can set originalElementHeight to the
             * currentlyFocusedOverlayElementHeight so that if we begin to shrink the element, we
             * can grow it back to the last size it was prior to being shrunken.
             */
            if (currentlyFocusedOverlayElementHeight < visibleHeightOffset &&
                visibleHeightOffset >
                    this.modifiers.elementHeightStatus.originalElementHeight) {
                this.modifiers.elementHeightStatus.originalElementHeight =
                    currentlyFocusedOverlayElementHeight;
            }
        }
    }
    setPosition() {
        if (this.source) {
            this.source.style.top = `${this.yPos}px`;
            this.source.style.left = `${this.xPos}px`;
            this.setPositionOfSmallTarget();
            if (this.modifiers.isTooltipOverlay) {
                this.source.style.zIndex = '1100';
            }
            // If there are more menu items than we can display on screen (in other words if menu overflows),
            // then we add a scroll to the menu
            if (this.modifiers.scrollToFit) {
                // Resetting height to 'auto'
                this.source.style.bottom = 'unset';
                const visibleHeightOffset = window.innerHeight - this.yPos - BOTTOM_PADDING;
                const currentlyFocusedOverlayElementHeight = this.source.getBoundingClientRect().height;
                this.determineIfOverlayElementCanResizeWithoutOverflow(currentlyFocusedOverlayElementHeight, visibleHeightOffset);
                if (currentlyFocusedOverlayElementHeight >= visibleHeightOffset) {
                    this.shrinkOverflowingOverlayElement(this.source, visibleHeightOffset);
                }
                else {
                    this.determineIfOverlayElementShouldGrow(this.source, currentlyFocusedOverlayElementHeight, visibleHeightOffset);
                }
            }
        }
    }
    getViewPortBoundaries() {
        const boundaries = {
            left: 0,
            right: window.innerWidth || document.documentElement.clientWidth,
            top: 0,
            bottom: window.innerHeight || document.documentElement.clientHeight,
        };
        // Add paddings
        const padding = this.modifiers.viewportPadding || 0;
        boundaries.left += padding;
        boundaries.top += padding;
        boundaries.right -= padding;
        boundaries.bottom -= padding;
        return boundaries;
    }
    getMouseBoundingClientRect() {
        if (this.modifiers.mouseCoordinates) {
            const x = this.modifiers.mouseCoordinates.mouseX;
            const y = this.modifiers.mouseCoordinates.mouseY;
            return {
                left: x,
                top: y,
                width: 0,
                height: 0,
            };
        }
    }
    /**
     * Returns true if the overlay content fits in the viewport
     */
    isValid(isOverflow = false) {
        const bounds = {
            left: this.xPos,
            right: this.xPos + this.sourceElBoundingRect.width,
            top: this.yPos,
            bottom: this.yPos + this.sourceElBoundingRect.height,
        };
        return this.isElementInViewport(bounds, isOverflow);
    }
    // hide/not show source when target is out of viewport
    // hide Element when source is of viewport
    isElementInViewport(element, isOverflow = false) {
        const { top, bottom, left, right } = this.viewportBoundaries;
        return (element.top >= top &&
            element.top <= bottom &&
            (isOverflow || element.bottom <= bottom) &&
            element.bottom >= top &&
            element.left >= left &&
            element.left <= right &&
            element.right <= right &&
            element.right >= left);
    }
    /**
     * if target is smaller in height or width than 25px, additional logic to place the popover in correct position.
     * POPOVER_TIP_OFFSET: 25px is 16px(distance from tip to the edge of the popover) + 9px(width/2 of tip)
     */
    setPositionOfSmallTarget() {
        if (this.modifiers.isPopoverOverlay &&
            (this.targetElBoundingRect.width / 2 < POPOVER_TIP_OFFSET ||
                this.targetElBoundingRect.height / 2 < POPOVER_TIP_OFFSET)) {
            switch (this.position) {
                case BOTTOM_LEFT:
                case TOP_LEFT:
                    if (this.targetElBoundingRect.width / 2 <
                        POPOVER_TIP_OFFSET)
                        this.source.style.left = `${this.xPos -
                            POPOVER_TIP_OFFSET +
                            this.targetElBoundingRect.width / 2}px`;
                    break;
                case RIGHT_BOTTOM:
                case LEFT_BOTTOM:
                    if (this.targetElBoundingRect.height / 2 <
                        POPOVER_TIP_OFFSET)
                        this.source.style.top = `${this.yPos +
                            POPOVER_TIP_OFFSET -
                            this.targetElBoundingRect.height / 2}px`;
                    break;
                case RIGHT_TOP:
                case LEFT_TOP:
                    if (this.targetElBoundingRect.height / 2 <
                        POPOVER_TIP_OFFSET)
                        this.source.style.top = `${this.yPos -
                            POPOVER_TIP_OFFSET +
                            this.targetElBoundingRect.height / 2}px`;
                    break;
                case BOTTOM_RIGHT:
                case TOP_RIGHT:
                    if (this.targetElBoundingRect.width / 2 <
                        POPOVER_TIP_OFFSET)
                        this.source.style.left = `${this.xPos +
                            POPOVER_TIP_OFFSET -
                            this.targetElBoundingRect.width / 2}px`;
                    break;
            }
        }
    }
    /**
     * Helper function to determine the y-pos of a flyout menu using the top and height properties
     * of the target and source element bounding rectangles
     *
     * @returns number that will be used to calculate the y-pos of the flyout menu to be rendered
     */
    determinePositionFromBoundingRects() {
        return (this.targetElBoundingRect.top +
            this.targetElBoundingRect.height -
            this.sourceElBoundingRect.height);
    }
    /**
     * Determines if the flyout menu that needs to be rendered has a RIGHT_TOP or LEFT_TOP position
     *
     * @param position location relative to the parent menu where flyout menus will be rendered
     * @returns true if flyout menu position is RIGHT_TOP or LEFT_TOP, false otherwise
     */
    isTopPositionedFlyoutMenu(position) {
        return position === RIGHT_TOP || position === LEFT_TOP;
    }
    /**
     * Ensures that flyout menus for inline menu and context menu are lined up with their parent menus
     *
     * @param position location relative to the parent menu where flyout menus will be rendered
     * @returns the y-position of the flyout menu that will be rendered
     */
    lineUpFlyoutMenus(position) {
        const isTopFlyout = this.isTopPositionedFlyoutMenu(position);
        if (isTopFlyout) {
            return this.targetElBoundingRect.top;
        }
        return this.determinePositionFromBoundingRects();
    }
    isPositionValid(position, isOverflow = false) {
        switch (position) {
            case BOTTOM_CENTER:
                this.xPos =
                    this.targetElBoundingRect.left +
                        this.targetElBoundingRect.width / 2 -
                        this.sourceElBoundingRect.width / 2;
                this.yPos =
                    this.targetElBoundingRect.top +
                        this.targetElBoundingRect.height +
                        this.padding;
                return this.isValid();
            case BOTTOM_LEFT:
                this.xPos = this.targetElBoundingRect.left;
                this.yPos =
                    this.targetElBoundingRect.top +
                        this.targetElBoundingRect.height +
                        this.padding;
                return this.isValid();
            case BOTTOM_RIGHT:
                this.xPos =
                    this.targetElBoundingRect.left +
                        this.targetElBoundingRect.width -
                        this.sourceElBoundingRect.width;
                this.yPos =
                    this.targetElBoundingRect.top +
                        this.targetElBoundingRect.height +
                        this.padding;
                return this.isValid();
            case TOP_CENTER:
                this.xPos =
                    this.targetElBoundingRect.left +
                        this.targetElBoundingRect.width / 2 -
                        this.sourceElBoundingRect.width / 2;
                this.yPos =
                    this.targetElBoundingRect.top -
                        this.sourceElBoundingRect.height -
                        this.padding;
                return this.isValid();
            case TOP_LEFT:
                this.xPos = this.targetElBoundingRect.left;
                this.yPos =
                    this.targetElBoundingRect.top -
                        this.sourceElBoundingRect.height -
                        this.padding;
                return this.isValid();
            case TOP_RIGHT:
                this.xPos =
                    this.targetElBoundingRect.left +
                        this.targetElBoundingRect.width -
                        this.sourceElBoundingRect.width;
                this.yPos =
                    this.targetElBoundingRect.top -
                        this.sourceElBoundingRect.height -
                        this.padding;
                return this.isValid();
            case RIGHT_BOTTOM:
            case RIGHT_TOP:
                this.xPos =
                    this.targetElBoundingRect.left -
                        (this.sourceElBoundingRect.width + this.padding);
                this.yPos = this.lineUpFlyoutMenus(position);
                return this.isValid(isOverflow);
            case RIGHT_CENTER:
                this.xPos =
                    this.targetElBoundingRect.left -
                        (this.sourceElBoundingRect.width + this.padding);
                this.yPos =
                    this.targetElBoundingRect.top +
                        this.targetElBoundingRect.height / 2 -
                        this.sourceElBoundingRect.height / 2;
                return this.isValid();
            case LEFT_BOTTOM:
            case LEFT_TOP:
                this.xPos =
                    this.targetElBoundingRect.left +
                        (this.targetElBoundingRect.width + this.padding);
                this.yPos = this.lineUpFlyoutMenus(position);
                return this.isValid(isOverflow);
            case LEFT_CENTER:
                this.xPos =
                    this.targetElBoundingRect.left +
                        (this.targetElBoundingRect.width + this.padding);
                this.yPos =
                    this.targetElBoundingRect.top +
                        this.targetElBoundingRect.height / 2 -
                        this.sourceElBoundingRect.height / 2;
                return this.isValid();
            default:
                return false;
        }
    }
}

const overlayPagePositionChange = 'overlayPagePositionChange';

var BxfOverlayTriggerEnum;
(function (BxfOverlayTriggerEnum) {
    BxfOverlayTriggerEnum["PRESSED"] = "pressed";
    BxfOverlayTriggerEnum["HOVER"] = "hover";
})(BxfOverlayTriggerEnum || (BxfOverlayTriggerEnum = {}));

const { OVERLAY_SOURCE, CLASSNAME, OVERLAY_CONTAINER, PARENT_CLASS_NAME, STYLE, NONE, PARENT, } = BxfPositionConstants.STRING;
const customWindow = window;
class BxfOverlayController {
    host;
    parentElement;
    targetElement;
    sourceElement;
    updateStateCallback;
    options;
    isOutsideOverlayCallback;
    updatePositionCallback;
    constructor(host, parentElement, targetElement, sourceElement, updateStateCallback, options = {}, isOutsideOverlayCallback, updatePositionCallback) {
        this.host = host;
        this.parentElement = parentElement;
        this.targetElement = targetElement;
        this.sourceElement = sourceElement;
        this.updateStateCallback = updateStateCallback;
        this.options = options;
        this.isOutsideOverlayCallback = isOutsideOverlayCallback;
        this.updatePositionCallback = updatePositionCallback;
        this._clickedOutside = this.onClickOutside.bind(this);
        this._mouseDownOutside = this.onMouseDownOutside.bind(this);
        this._mouseUpOutside = this.onMouseUpOutside.bind(this);
        this._escapeKeyHandler = this.escapeKeyHandler.bind(this);
        this._keyDownHandler = this.keyDownHandler.bind(this);
        this._onHoverInside = this.onHoverInside.bind(this);
        this._onHoverOutside = this.onHoverOutside.bind(this);
        this._spaceKeyHandler = this.spaceKeyHandler.bind(this);
        this._mouseDownHandler = this.mouseDownHandler.bind(this);
        this._mouseUpHandler = this.mouseUpHandler.bind(this);
        this._openOverlay = this.open.bind(this);
        this._updateOnViewportChange = this.updateOnViewportChange.bind(this);
        this._closePopover = this.close.bind(this, true);
        this.create();
    }
    _onHoverInside;
    _onHoverOutside;
    _hoverOutTimeout;
    _hoverInTimeout;
    _closePopover;
    _clickedOutside;
    _mouseDownOutside;
    _mouseUpOutside;
    _openOverlay;
    _updateOnViewportChange;
    _escapeKeyHandler;
    _keyDownHandler;
    _spaceKeyHandler;
    _mouseDownHandler;
    _mouseUpHandler;
    _initiallyFocusedElem;
    _targetClicked = false;
    windowViewportEventHandlers = [];
    windowViewportEventHandlerNames = [
        WindowEventMap.scroll,
        WindowEventMap.resize,
        overlayPagePositionChange,
    ];
    _clickOutsideStarted = false;
    _previousFocusTrap;
    create() {
        if (this.sourceElement &&
            this.targetElement &&
            this.parentElement &&
            this.updateStateCallback) {
            this.init();
        }
        return this;
    }
    updatePosition() {
        if (!this.isOpen()) {
            this.open();
        }
        this.renderOverlay();
        return this;
    }
    /**
     * Add or remove mouse focus class to event target based on event type.
     * @param {Event} event
     */
    checkEventPathForFocus(event) {
        const path = eventPath(event);
        const keepFocus = this.shouldReturnFocusOnMouseClick();
        const isKeyboardClick = isKeyboardClickEvent(event);
        if (path) {
            const pressItem = find(path, (elem) => elem.tagName &&
                (elem.tagName.toLowerCase() === 'button' ||
                    elem.tagName.toLowerCase() === 'a'));
            /**
             * Skip adding BXF_MOUSE_FOCUS if a target exists with BXF_FOCUS_KEYBOARD_ONLY
             * 	to smoothen transition to :focus-visible selector
             * @see ADO-1343083
             */
            if (pressItem?.classList &&
                !pressItem.classList.contains(BXF_FOCUS_KEYBOARD_ONLY)) {
                if ((event instanceof KeyboardEvent ||
                    isKeyboardClick ||
                    keepFocus) &&
                    pressItem.classList.contains(BXF_MOUSE_FOCUS)) {
                    pressItem.classList.remove(BXF_MOUSE_FOCUS);
                }
                else if (event instanceof MouseEvent &&
                    !isKeyboardClick &&
                    !keepFocus &&
                    !pressItem.classList.contains(BXF_MOUSE_FOCUS)) {
                    pressItem.classList.add(BXF_MOUSE_FOCUS);
                }
            }
        }
    }
    /**
     * Handles opening overlay
     */
    open(event) {
        let shouldOpen = true;
        if (has(this.options, 'shouldOpenCallback')) {
            shouldOpen = this.options.shouldOpenCallback();
        }
        if (!this.isOpen() && shouldOpen) {
            this.initViewportChangeHandler();
            if (event) {
                event.stopPropagation();
                if (!get(this.options, 'useFocusVisible', false)) {
                    this.checkEventPathForFocus(event);
                }
            }
            // Close other open overlays before opening the current overlay
            map(this.options.overlaysToClose, (containerClassName) => this.closeAllOverlays(containerClassName));
            this.updateStateCallback(true);
        }
    }
    /**
     * Context for overlay focus trap.
     */
    get focusTrapContext() {
        return this.sourceElement;
    }
    /**
     * Get initially focused element to return focus to when overlay closed.
     * @protected
     * @returns {HTMLElement}
     */
    getInitiallyFocusedElem() {
        return FocusTrap.getFocusedElem(this.targetElement);
    }
    openOverlayWithPosition() {
        this._initiallyFocusedElem = this.getInitiallyFocusedElem();
        this.openOverlayContainer();
        this.handleKeyDown();
        if (this.shouldCloseOnEscape()) {
            this.handleClosingOnEscape();
        }
        if (this.shouldCloseOnMouseUp()) {
            document.addEventListener(WindowEventMap.mousedown, this._mouseDownOutside);
            document.addEventListener(WindowEventMap.mouseup, this._mouseUpOutside);
        }
        else {
            document.addEventListener(WindowEventMap.mousedown, this._clickedOutside);
        }
    }
    /**
     * Decide whether or not to move focus based on skipFocus value
     */
    defaultFocusCallback() {
        return !get(this.options, 'skipFocus', false);
    }
    /**
     * Decide whether or not to move focus after open:
     * 	- If custom focus callback passed, use callback function
     * 	- Otherwise, use default method (check skipFocus)
     */
    shouldMoveFocus() {
        return this.options?.shouldFocusCallback
            ? this.options.shouldFocusCallback()
            : this.defaultFocusCallback();
    }
    /**
     * Whether or not to close overlay on escape
     */
    shouldCloseOnEscape() {
        return get(this.options, 'closeOnEscape', true);
    }
    /**
     * Whether or not to handle arrow keys
     */
    shouldFireArrowKeyCallback() {
        return !!get(this.options, 'arrowKeyCallback', null);
    }
    /**
     * Whether or not to return focus on mouse click
     */
    shouldReturnFocusOnMouseClick() {
        return get(this.options, 'keepMouseFocus', false);
    }
    /**
     * Whether or not to attach/detach keyboard listeners.
     * Attach/detach if any of the following are true:
     * - Overlay should close on escape
     * - Focus should be trapped in overlay
     * - Arrow key callback is passed in
     */
    shouldHandleKeyDown() {
        return (this.shouldCloseOnEscape() ||
            this.shouldMoveFocus() ||
            this.shouldFireArrowKeyCallback());
    }
    /**
     * Whether or not both mousedown and mouseup should occur outside
     * 	overlay in order for overlay to close, default to false
     */
    shouldCloseOnMouseUp() {
        return get(this.options, 'shouldCloseOnMouseUp', false);
    }
    /**
     * Add event listener for tab, arrow and escape to overlay if keydown should be handled
     */
    handleKeyDown() {
        if (this.focusTrapContext && this.shouldHandleKeyDown()) {
            this.focusTrapContext.addEventListener(WindowEventMap.keydown, this._keyDownHandler);
            this._previousFocusTrap = customWindow.activeFocusTrap;
            customWindow.activeFocusTrap = this.focusTrapContext;
        }
    }
    /**
     * Remove event listener for keydown if attached to overlay
     */
    removeKeyDownHandler() {
        if (this.focusTrapContext && this.shouldHandleKeyDown()) {
            this.focusTrapContext.removeEventListener(WindowEventMap.keydown, this._keyDownHandler);
            customWindow.activeFocusTrap = this._previousFocusTrap;
        }
    }
    /**
     * Close overlay on escape and stop event from propagating up
     * 	if shouldStopPropagation = true
     */
    escapeKeyHandler(event, shouldStopPropagation = false) {
        if (FocusIndexManager.ESC_KEYS.includes(event.key)) {
            if (shouldStopPropagation) {
                event.stopPropagation();
            }
            this.close();
        }
    }
    /**
     * Handle the following keydown events:
     * - Tab - trap focus
     * - Escape
     * - Arrow keys
     */
    keyDownHandler(event) {
        if (this.shouldCloseOnEscape()) {
            this.escapeKeyHandler(event, true);
        }
        if (this.shouldFireArrowKeyCallback()) {
            this.options.arrowKeyCallback(event);
        }
        if (event.key === KeyboardEventKey.Tab && this.shouldMoveFocus()) {
            FocusTrap.trapFocus(event, this.focusTrapContext);
        }
    }
    /**
     * Add document level escape listener to allow closing on escape when
     * 	overlay is out of focus (e.g. user click).
     */
    handleClosingOnEscape() {
        document.addEventListener(WindowEventMap.keydown, this._escapeKeyHandler);
    }
    /**
     * Open overlay when space bar pressed (universal press for buttons/links/etc)
     * @param {KeyboardEvent} event
     */
    spaceKeyHandler(event) {
        if (event.key === ' ' || event.key === 'Spacebar') {
            this.open(event);
        }
    }
    /**
     * If the overlay isn't already open, set _targetClicked to true
     * 	so that the overlay will open when mouseup fires on the target
     */
    mouseDownHandler() {
        if (!this.isOpen()) {
            this._targetClicked = true;
        }
    }
    /**
     * Opens overlay on mouseup and resets _targetClicked state
     * 	if a mousedown event already occurred on the target.
     */
    mouseUpHandler(event) {
        if (this._targetClicked) {
            this.open(event);
            this._targetClicked = false;
        }
    }
    /**
     * Remove document-level escape listener if closing on escape enabled
     */
    removeEscapeListener() {
        if (this.shouldCloseOnEscape()) {
            document.removeEventListener(WindowEventMap.keydown, this._escapeKeyHandler);
        }
    }
    removeClickOutsideListener() {
        if (this.shouldCloseOnMouseUp()) {
            document.removeEventListener(WindowEventMap.mousedown, this._mouseDownOutside);
            document.removeEventListener(WindowEventMap.mouseup, this._mouseUpOutside);
        }
        else {
            document.removeEventListener(WindowEventMap.mousedown, this._clickedOutside);
        }
    }
    /**
     * Whether or not to return focus to first element on close
     */
    shouldFocusFirstElement(isMouseFocus) {
        return (this._initiallyFocusedElem &&
            (!isMouseFocus || this.shouldReturnFocusOnMouseClick()) &&
            this.shouldMoveFocus() &&
            !get(this.options, 'skipFocusOnClose', false) &&
            !get(this.options, 'isFlyoutMenu', false) &&
            (this.options.trigger !== BxfOverlayTriggerEnum.HOVER ||
                (this.options.targetElementOptions &&
                    !isEmpty(this.options.targetElementOptions.targetEventsMap))));
    }
    /**
     * Focus first element inside target slot if focus should be moved.
     */
    focusFirstElement(isMouseFocus) {
        if (this.shouldFocusFirstElement(isMouseFocus)) {
            FocusTrap.focusFirstElem(this._initiallyFocusedElem, isMouseFocus);
        }
    }
    /**
     * Handles closing overlay
     * @param isMouseFocus whether or not to hide focus ring
     */
    close(isMouseFocus = false) {
        let shouldClose = true;
        if (has(this.options, 'shouldCloseCallback')) {
            shouldClose = this.options.shouldCloseCallback();
        }
        // Check if there are flyout menus / child overlays open
        const isChildOverlayOpen = has(this.options, 'isChildOverlayOpenCallback') &&
            this.options.isChildOverlayOpenCallback();
        if (!isChildOverlayOpen && this.isOpen() && shouldClose) {
            this.destroyViewportChangeHandler();
            this.closeOverlayContainer();
            if (this.sourceElement) {
                this.parentElement.appendChild(this.sourceElement);
                this.focusFirstElement(isMouseFocus);
            }
            this.updateStateCallback(false);
        }
        if (!this.isOpen()) {
            this.removeClickOutsideListener();
            this.removeKeyDownHandler();
            this.removeEscapeListener();
        }
    }
    init() {
        this.initOptions();
        this.initTargetElement();
        this.initSourceElement();
    }
    initOptions() {
        const DEFAULT_OPTIONS = {
            trigger: BxfOverlayTriggerEnum.PRESSED,
            hoverInTimeout: HOVER_IN_DELAY,
            overlaysToClose: [],
            shouldOpenCallback: () => true,
            closeOnEscape: true,
            shouldToggleExpanded: true,
        };
        this.options = {
            ...DEFAULT_OPTIONS,
            ...this.options,
            ...this.initModifiers(),
            ...this.initSourceElementOptions(),
            ...this.initTargetElementOptions(),
        };
    }
    initModifiers() {
        const { BOTTOM_CENTER } = BxfDynamicPositionEnum;
        const DEFAULT_MODIFIERS = {
            isOnCursorPosition: false,
            flipOrder: DefaultFlipOrder,
            defaultPlacement: BOTTOM_CENTER,
            padding: 0,
        };
        return {
            modifiers: {
                ...DEFAULT_MODIFIERS,
                ...this.options.modifiers,
            },
        };
    }
    initSourceElementOptions() {
        const DEFAULT_OPTIONS = {
            overlayClassName: OVERLAY_SOURCE,
            shouldAttachEventFromTrigger: true,
        };
        return {
            sourceElementOptions: {
                ...DEFAULT_OPTIONS,
                ...this.options.sourceElementOptions,
            },
        };
    }
    initTargetElementOptions() {
        const DEFAULT_OPTIONS = {
            targetForEventListeners: this.targetElement,
            targetEventType: [WindowEventMap.mousedown],
        };
        return {
            targetElementOptions: {
                ...DEFAULT_OPTIONS,
                ...this.options.targetElementOptions,
            },
        };
    }
    /**
     * Initialize source element which will be opened as a overlay.
     */
    initSourceElement() {
        this.sourceElement.classList.add(OVERLAY_SOURCE);
        this.sourceElement.style.display = NONE;
        this.sourceElement['hostRef'] = this.host;
        if (this.options.trigger === BxfOverlayTriggerEnum.HOVER &&
            this.options.sourceElementOptions.shouldAttachEventFromTrigger) {
            this.sourceElement.addEventListener(WindowEventMap.mouseover, this._onHoverInside);
            this.sourceElement.addEventListener(WindowEventMap.mouseout, this._onHoverOutside);
        }
        const { sourceElementOptions } = this.options;
        if (sourceElementOptions) {
            map(sourceElementOptions.addClassNames, (name) => this.sourceElement.classList.add(name));
        }
    }
    /**
     * Initialize target element which will trigger opening overlay.
     */
    initTargetElement() {
        if (this.options.targetElementOptions.targetForEventListeners) {
            switch (this.options.trigger) {
                case BxfOverlayTriggerEnum.HOVER:
                    this.hoverTriggerHandler(this.options.targetElementOptions
                        .targetForEventListeners);
                    break;
                case BxfOverlayTriggerEnum.PRESSED:
                    this.pressedTriggerHandler(this.options.targetElementOptions
                        .targetForEventListeners);
                    break;
                // no default
            }
        }
        const { targetElementOptions } = this.options;
        if (targetElementOptions) {
            map(targetElementOptions.addClassNames, (name) => this.targetElement.classList.add(name));
        }
        const targetElementEvents = get(this.options.targetElementOptions, 'targetEventsMap');
        if (targetElementEvents) {
            this.customEventsHandler(this.options.targetElementOptions.targetForEventListeners, targetElementEvents);
        }
    }
    customEventsHandler(targetForEventListeners, 
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    targetEventsMap) {
        forEach(targetEventsMap, (value, key) => {
            addEventListenerOnElement(targetForEventListeners, key, value);
        });
    }
    hoverTriggerHandler(targetForEventListeners) {
        addEventListenerOnElement(targetForEventListeners, WindowEventMap.mouseover, this._onHoverInside);
        addEventListenerOnElement(targetForEventListeners, WindowEventMap.mouseout, this._onHoverOutside);
    }
    pressedTriggerHandler(targetForEventListeners) {
        if (this.options.targetElementOptions.targetEventType.includes(WindowEventMap.click)) {
            addEventListenerOnElement(targetForEventListeners, WindowEventMap.click, this._openOverlay);
        }
        else if (this.options.targetElementOptions.targetEventType.includes(WindowEventMap.mousedown)) {
            addEventListenerOnElement(targetForEventListeners, WindowEventMap.mousedown, this._openOverlay);
            // add touchscreen and keyboard equivalents to mousedown
            addEventListenerOnElement(targetForEventListeners, WindowEventMap.touchstart, this._openOverlay);
            addEventListenerOnElement(targetForEventListeners, WindowEventMap.keyup, this._spaceKeyHandler);
        }
        else if (this.options.targetElementOptions.targetEventType.includes(WindowEventMap.mouseup)) {
            addEventListenerOnElement(targetForEventListeners, WindowEventMap.keyup, this._spaceKeyHandler);
            addEventListenerOnElement(targetForEventListeners, WindowEventMap.mousedown, this._mouseDownHandler);
            addEventListenerOnElement(targetForEventListeners, WindowEventMap.mouseup, this._mouseUpHandler);
        }
    }
    initViewportChangeHandler() {
        if (!includes(this.windowViewportEventHandlers, this.windowViewportEventHandlerNames)) {
            window.addEventListener(WindowEventMap.scroll, (event) => {
                // Allows internal scrolling for popovers, but if window scrolls then we close popover
                if (event.target === document &&
                    this.options.shouldPopoverScroll) {
                    this._updateOnViewportChange();
                }
            }, true);
            window.addEventListener(WindowEventMap.resize, this._updateOnViewportChange);
            window.addEventListener(overlayPagePositionChange, this.updateOnViewportChangeAsync);
            // Special case for chat canvas scroll container
            if (this.options.isChatCanvasOverlay) {
                const { CANVAS, SCROLL_CONTAINER_ID } = BxfChatCanvasConstants;
                const chatCanvasScrollContainer = document
                    .querySelector(CANVAS)
                    ?.shadowRoot?.getElementById(SCROLL_CONTAINER_ID);
                chatCanvasScrollContainer?.addEventListener(WindowEventMap.scroll, this._updateOnViewportChange, true);
            }
            this.windowViewportEventHandlers.push(this.windowViewportEventHandlerNames);
        }
    }
    destroyViewportChangeHandler() {
        window.removeEventListener(WindowEventMap.scroll, this._updateOnViewportChange, true);
        window.removeEventListener(WindowEventMap.resize, this._updateOnViewportChange);
        window.removeEventListener(overlayPagePositionChange, this.updateOnViewportChangeAsync);
        this.windowViewportEventHandlers.length = 0;
    }
    updateOnViewportChangeAsync = () => new Promise((resolve) => {
        const asyncCall = setTimeout(() => {
            this._updateOnViewportChange();
            clearTimeout(asyncCall);
            resolve();
        }, 0);
    });
    /**
     * Once overlay is appended to end of body, update it's position to show it in correct position
     */
    renderOverlay() {
        if (get(this.options, 'modifiers.isOnCursorPosition') &&
            has(this.options, 'getMouseCoordinatesCallback')) {
            const mouseCoordinates = this.options.getMouseCoordinatesCallback();
            this.renderOverlayWithMousePosition(mouseCoordinates);
        }
        else {
            this.renderOverlayWithPosition();
        }
    }
    /**
     * On window resize and scroll, update the position of popover
     */
    updateOnViewportChange() {
        if (this.options?.shouldCloseOnScroll) {
            this.close();
            this._closePopover();
            return;
        }
        if (this.isOpen()) {
            this.renderOverlay();
        }
    }
    /**
     * Updates popover element's position based on viewport
     */
    renderOverlayWithPosition() {
        if (this.sourceElement && this.targetElement) {
            /*
             * Ensure all the event handlers that monitor viewport changes are added to the window
             * before we try to update the overlay position
             */
            this.initViewportChangeHandler();
            const position = new BxfDynamicPosition(this.options.modifiers, this.sourceElement, this.targetElement);
            if (this.updatePositionCallback) {
                this.updatePositionCallback(position);
            }
        }
    }
    /**
     * Updates popover element's position based on mouse Coordinates
     */
    renderOverlayWithMousePosition(mouseCoordinates) {
        const NEW_MOUSE_MODIFIERS = {
            isOnCursorPosition: true,
            mouseCoordinates: {
                mouseX: mouseCoordinates.mouseX,
                mouseY: mouseCoordinates.mouseY,
            },
        };
        if (!isEmpty(mouseCoordinates)) {
            this.options.modifiers = {
                ...this.options.modifiers,
                ...NEW_MOUSE_MODIFIERS,
            };
            if (this.sourceElement && this.targetElement) {
                const position = new BxfDynamicPosition(this.options.modifiers, this.sourceElement, this.targetElement);
                if (this.updatePositionCallback) {
                    this.updatePositionCallback(position);
                }
            }
        }
    }
    /**
     * Returns hoverInDelay value in ms
     */
    getHoverInDelay = () => Number.isNaN(Number(this.options.hoverInTimeout))
        ? 50
        : this.options.hoverInTimeout;
    /**
     * Returns hoverOutDelay value in ms
     * 'hoverOutDelay' should be greater than 0 and less than 'hoverInDelay'
     * to prevent cases when user quickly hover from one item with popover to another one
     */
    getHoverOutDelay = () => Math.min(this.getHoverInDelay(), HOVER_IN_DELAY) - 10;
    /**
     * Opens overlay when target is hovered inside
     */
    onHoverInside() {
        window.clearTimeout(this._hoverOutTimeout);
        this._hoverInTimeout = window.setTimeout(this._openOverlay, this.getHoverInDelay());
    }
    /**
     * Close overlay when target is hovered outside
     */
    onHoverOutside() {
        window.clearTimeout(this._hoverInTimeout);
        this._hoverOutTimeout = window.setTimeout(this._closePopover, this.getHoverOutDelay());
    }
    /**
     * Check if mouse event is outside overlay
     */
    isEventOutsideOverlay(event) {
        return this.isOutsideOverlayCallback
            ? this.isOutsideOverlayCallback(event)
            : this.defaultIsOutsideOverlay(event);
    }
    /**
     * Close overlay and return focus only if keepMouseFocus is true
     */
    handleMouseClose() {
        this.close(!this.shouldReturnFocusOnMouseClick());
    }
    /**
     * Handle click on anywhere in document. If click is not inside overlay, close all popovers.
     */
    onClickOutside(event) {
        if (this.isEventOutsideOverlay(event)) {
            this.handleMouseClose();
        }
    }
    /**
     * On mousedown, flag click start as inside or outside overlay
     */
    onMouseDownOutside(event) {
        this._clickOutsideStarted = this.isEventOutsideOverlay(event);
    }
    /**
     * Close overlay on mouseup if both mousedown and mouseup occurred outside
     */
    onMouseUpOutside(event) {
        if (this._clickOutsideStarted && this.isEventOutsideOverlay(event)) {
            this._clickOutsideStarted = false;
            this.handleMouseClose();
        }
    }
    /**
     * Determine if click is inside or outside popover
     */
    defaultIsOutsideOverlay(event) {
        const classMap = map(eventPath(event), CLASSNAME);
        /**
         * NOTE: need to explicitly default to OVERLAY_SOURCE to avoid empty
         * 	class names getting picked up
         */
        return isEmpty(filter(classMap, (value) => includes(value, get(this.options, 'sourceElementOptions.overlayClassName', OVERLAY_SOURCE))));
    }
    isOpen(element = this.sourceElement) {
        return !!(get(element, PARENT) &&
            element.style.top &&
            element.style.display !== 'none');
    }
    openOverlayContainer() {
        if (get(this.sourceElement, PARENT_CLASS_NAME) === OVERLAY_CONTAINER) {
            return;
        }
        if (this.sourceElement) {
            if (!this.shouldOpenOverlayWithMouseCoordinates()) {
                return;
            }
            this.appendToContainer(this.sourceElement);
            if (this.shouldMoveFocus()) {
                this.moveFocusToOverlay();
            }
            this.sourceElement.removeAttribute(STYLE);
        }
        this.renderOverlay();
    }
    /**
     * Determine if overlay should be appended in body.
     * To handle a scenario where when a tooltip is opened and we scroll without moving mouse to a new target,
     * the new tooltip opens at the top
     */
    shouldOpenOverlayWithMouseCoordinates() {
        if (get(this.options, 'modifiers.isOnCursorPosition') &&
            has(this.options, 'getMouseCoordinatesCallback')) {
            return !!this.options.getMouseCoordinatesCallback();
        }
        return true;
    }
    closeOverlayContainer() {
        const parentElement = get(this.sourceElement, PARENT);
        if (parentElement) {
            this.sourceElement.removeAttribute(STYLE);
            this.sourceElement.style.display = NONE;
            removeFromContainer(this.sourceElement, this.options.parentContainer || document.body);
        }
    }
    createOverlayContainer(containerClassName = OVERLAY_CONTAINER) {
        if (!elementExists(containerClassName)) {
            (this.options.parentContainer || document.body).appendChild(createElementWithClass(containerClassName));
        }
        const element = getElement(OVERLAY_CONTAINER);
        element.style.zIndex = has(this.options, 'modifiers.zIndex')
            ? this.options.modifiers.zIndex
            : DEFAULT_Z_INDEX;
    }
    /**
     * Get item to focus when overlay opened.
     * NOTE: overridden in BxfOverlayDropdownController
     * @see BxfOverlayDropdownController
     */
    getItemToFocus() {
        return this.focusTrapContext;
    }
    moveFocusToOverlay() {
        setTimeout(() => {
            const itemToFocus = this.getItemToFocus();
            if (itemToFocus) {
                itemToFocus.tabIndex = -1;
                itemToFocus.focus();
            }
        });
    }
    appendToContainer(element, containerClassName = OVERLAY_CONTAINER) {
        if (elementExists(containerClassName)) {
            getElement(containerClassName).appendChild(element);
        }
        else {
            getElement(OVERLAY_CONTAINER).appendChild(createElementWithClass(containerClassName));
            this.appendToContainer(element, containerClassName);
        }
    }
    /**
     * Handle closing all overlays anywhere in document
     */
    closeAllOverlays(containerClassName = OVERLAY_CONTAINER) {
        const elements = Array.from(document.getElementsByClassName(containerClassName));
        if (elements[0]) {
            map(elements[0].children, (value) => {
                if (value.hostRef &&
                    this.host !== value.hostRef &&
                    value.hostRef.close)
                    value.hostRef.close();
            });
        }
    }
}

export { BxfDynamicPosition as B, BxfOverlayController as a, BxfOverlayTriggerEnum as b, overlayPagePositionChange as o };
